import { useState, useEffect } from "react";
import {
  Typography,
} from "@mui/material";
import React from "react";
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import { GridColDef } from '@mui/x-data-grid';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import axios from "axios";
import { alpha, styled } from '@mui/material/styles';
import { DataGrid, gridClasses } from '@mui/x-data-grid';
import { IApplication } from "../interfaces/IApplication";
import { setPageTitle } from "../slices/applicationSlice";
import { useAppDispatch } from "../hooks";
import SearchChips from "../components/SearchChips";
import { ISearchChips } from "../interfaces/Core/ISearchChips";
import moment from "moment";
import ActionButton from "../components/ActionButton";
import ActionMenu from "../components/ActionMenu";

type Anchor = 'top' | 'left' | 'bottom' | 'right';

const ODD_OPACITY = 0.2;

const StripedDataGrid = styled(DataGrid)(({ theme }) => ({
  [`& .${gridClasses.row}.even`]: {
    backgroundColor: theme.palette.grey[300],
    '&:hover, &.Mui-hovered': {
      backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
    '&.Mui-selected': {
      backgroundColor: alpha(
        theme.palette.primary.main,
        ODD_OPACITY + theme.palette.action.selectedOpacity,
      ),
      '&:hover, &.Mui-hovered': {
        backgroundColor: alpha(
          theme.palette.primary.main,
          ODD_OPACITY +
          theme.palette.action.selectedOpacity +
          theme.palette.action.hoverOpacity,
        ),
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: alpha(
            theme.palette.primary.main,
            ODD_OPACITY + theme.palette.action.selectedOpacity,
          ),
        },
      },
    },
  },
}));

export default function PatchingProduction() {

  function ButtonClicked(_event: React.MouseEvent<HTMLElement>): void {
    
    setOpen(true);
  }

  const [open, setOpen] = useState(false);

  function handleClose() {
    setOpen(!open);
  }

  const buttons = [
    <ActionButton action={ButtonClicked} buttonLabel="Download report" />,
    
  ];


  const dispatch = useAppDispatch();

  useEffect(() => {
    let appState: IApplication = {
      pageTitle: "Patching Production",
      backgroundPath: "/assets/images/patchingbg.jpg"
    }
    dispatch(setPageTitle(appState));
  }, []);

  const [state, setState] = React.useState({
    top: false,
    left: false,
    bottom: false,
    right: false,
  });

  interface IShortageExtraRow {
    species: string,
    variety: string,
    color: string,
    expectedCount: number,
    buffer: number,
    shortageExtra: number,
    crew: string,
    startTime: Date,
    endTime: Date
  }

  const [rows, setRows] = useState<IShortageExtraRow[]>([]);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_ENDPOINT}/api/CombifixBatches`)
      .then(res => {
        setRows(res.data);
      })
  }, []);

  const columns: GridColDef[] = [
    //{ field: 'id', headerName: 'ID', width: 50 },
    {
      field: 'batchNrPlc', headerName: 'batchNrPlc', width: 100,
    },
    { field: 'memo', headerName: 'Memo', width: 130 },
    {
      field: 'startTime', headerName: 'Start Time', width: 130, valueFormatter: params =>
        moment(params?.value).format("DD/MM/YYYY hh:mm A"),
    },
    {
      field: 'endTime', headerName: 'End Time', width: 175, valueFormatter: params =>
        moment(params?.value).format("DD/MM/YYYY hh:mm A"),
    },
    { field: 'traysFixed', headerName: 'Fixed', width: 120, },
    { field: 'traysEmptied', headerName: 'Emptied', width: 160, },
    { field: 'traysPartial', headerName: 'Partial', width: 150, },
    { field: 'totalPlantsCatNone', headerName: 'Total Cat None', width: 150, },
    { field: 'totalPlantsCat1', headerName: 'Total Cat 1', width: 150, },
    { field: 'totalPlantsCat2', headerName: 'Total Cat 2', width: 150, },
    { field: 'totalPlantsCat3', headerName: 'Total Cat 3', width: 150, },
    { field: 'totalPlantsCat4', headerName: 'Total Cat 4', width: 150, },
    { field: 'pctPlantsCatNone', headerName: 'PCT Cat None', width: 110, },
    { field: 'pctPlantsCat1', headerName: 'PCT Cat 1', width: 110, },
    { field: 'pctPlantsCat2', headerName: 'PCT Cat 2', width: 110, },
    { field: 'pctPlantsCat3', headerName: 'PCT Cat 3', width: 110, },
    { field: 'pctPlantsCat4', headerName: 'PCT Cat 4', width: 110, },
    {
      field: 'createdAt', headerName: 'Created', width: 175, valueFormatter: params =>
        moment(params?.value).format("DD/MM/YYYY hh:mm A"),
    },
    {
      field: 'updatedAt', headerName: 'Updated', width: 175, valueFormatter: params =>
        moment(params?.value).format("DD/MM/YYYY hh:mm A"),
    }
  ];

  const [trayrows, setTrayRows] = useState<IShortageExtraRow[]>([]);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_ENDPOINT}/api/CombifixTrays`)
      .then(res => {
        setTrayRows(res.data);
      })
  }, []);

  const traycolumns: GridColDef[] = [
    //{ field: 'id', headerName: 'ID', width: 50 },
    { field: 'batchID', headerName: 'Batch ID', width: 130 },
    { field: 'batchStartTime', headerName: 'Start Time', width: 130 },
    { field: 'timeStamp', headerName: 'Time Stamp', width: 130, },
    { field: 'trayNrPlc', headerName: 'Tray NR PLC', width: 120, },
    { field: 'badPlugs', headerName: 'Bad Plugs', width: 120, },
    { field: 'goodPlugs', headerName: 'Good Plugs', width: 160, },
    { field: 'pctBadPlugs', headerName: 'Bad Plug %', width: 150, },
    { field: 'pctGoodPlugs', headerName: 'Good Plug %', width: 90, },
    { field: 'status', headerName: 'Status', width: 110, },
  ];

  const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: theme.palette.mode === 'dark' ? '#010101' : '#fff',
    ...theme.typography.body2,
    padding: theme.spacing(1),
    textAlign: 'center',
    color: theme.palette.text.secondary,
    border: 0,
    borderRadius: 0,
    boxShadow: "none",
  }));

  function valuetext(value: number) {
    return `${value}°C`;
  }

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover,
    },
    // hide last border
    '&:last-child td, &:last-child th': {
      border: 0,
    },
  }));

  const darkTheme = createTheme({
    palette: {
      mode: 'dark',
      primary: {
        main: '#89B219',
      },
    },
  });

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: theme.palette.common.black,
      color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
    },
  }));

  const handleDelete = () => {
    console.info('You clicked the delete icon.');
  };

  const [helpOpen, setHelpOpen] = React.useState(false);

  const handleClickHelpOpen = () => {
    setHelpOpen(true);
  };

  const handleHelpClose = () => {
    setHelpOpen(false);
  };

  const [searchInformation, setSearchInformation] = React.useState<ISearchChips>();

  const handleSearchChips = (searchInformation: ISearchChips) => {
    setSearchInformation(searchInformation);
  }


  return (
    <div>
     <ActionMenu buttons={buttons} />
      <Grid container spacing={0} bgcolor="#101010" >
        <ThemeProvider theme={darkTheme}>
          <SearchChips handleSearch={handleSearchChips}></SearchChips>
        </ThemeProvider>
        <Grid item xs={12}>
          <Item>
            <div style={{ height: 'calc(100vh - 135px)', width: '100%' }}>
              <StripedDataGrid
                rows={rows}
                columns={columns}
                pageSize={20}
                rowsPerPageOptions={[20]}
                checkboxSelection
                sx={{ borderColor: 'rgba(100,100,100,0.2)' }}
                getRowClassName={(params) =>
                  params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
                }
              />
            </div>
          </Item>
        </Grid>
        <Grid item xs={12}>
          <Item><Typography textAlign='left' >Combifix Trays</Typography></Item>
          <Item>
            <div style={{ height: 'calc(100vh - 135px)', width: '100%' }}>
              <DataGrid
                rows={trayrows}
                columns={traycolumns}
                pageSize={20}
                rowsPerPageOptions={[20]}
                checkboxSelection
                sx={{ borderColor: 'rgba(100,100,100,0.2)' }}
              />
            </div>
          </Item>
        </Grid>
      </Grid>
    </div>
  );
}